import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { Target, TrendingUp, Users, DollarSign, Zap, CheckCircle, AlertCircle, Lightbulb, Save, Sparkles } from 'lucide-react';
import { supabase } from '../../integrations/supabase/client';
import { dataService } from '@/services/dataService';
import { useAuth } from '../../hooks/useAuth';
import { useUserData } from '@/hooks/useUserData';
import { openAIService } from '@/services/openaiService';
import { SummaryCard } from './summary-card';
import { replace } from 'react-router-dom';

interface IdeaAssessmentData {
  id?: string;
  user_id: string;
  idea_description: string;
  target_audience: string;
  problem_solved: string;
  revenue_model: string;
  competition: string;
  why_people_pay: string;
  ai_feedback?: any;
  created_at?: string;
  updated_at?: string;
  status?: 'draft' | 'in_progress' | 'completed';
}

// Types used by AI parsing and UI
interface AnalysisResults {
  viabilityScore: number;
  marketPotential: string;
  competitionLevel: string;
  recommendations: string[];
  sources?: string[];
}

interface DetailedAssessment {
  [key: string]: any; // Add index signature for Supabase JSON compatibility
  swot?: {
    strengths: string[];
    weaknesses: string[];
    opportunities: string[];
    threats: string[];
  };
  mvpSteps?: string[];
  reachTarget?: string[];
  marketDemand?: {
    summary: string;
    signals: string[];
  };
  marketCompetition?: {
    summary: string;
    competitors: Array<{name: string; note: string}>;
  };
  frameworks?: {
    leanCanvas: string[];
    aarr: string[];
  };
  actionPlan?: {
    next30Days: string[];
    risks: string[];
    mitigations: string[];
  };
  sources?: string[];
}

// Helper: sanitize and parse JSON returned by models  // Enhanced JSON parsing with multiple strategies
const parseSafeJson = <T = any>(raw: string): T => {
  if (!raw || typeof raw !== 'string') {
    throw new Error('Invalid input: expected non-empty string');
  }

  console.log('Raw AI response:', raw);

  // Strategy 1: Try direct parsing first
  try {
    return JSON.parse(raw.trim());
  } catch (e) {
    // Continue to other strategies
  }

  // Strategy 1.5: Handle assistantfinal pattern immediately
  if (raw.includes('assistantfinal{')) {
    const startIndex = raw.indexOf('assistantfinal{') + 'assistantfinal'.length;
    const jsonPart = raw.substring(startIndex);
    try {
      return JSON.parse(jsonPart);
    } catch (e) {
      console.log('assistantfinal parsing failed:', e);
    }
  }

  // Strategy 2: Extract JSON from code blocks
  const codeBlockMatch = raw.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
  if (codeBlockMatch) {
    try {
      return JSON.parse(codeBlockMatch[1]);
    } catch (e) {
      // Continue to other strategies
    }
  }

  // Strategy 4: Extract JSON from mixed content (analysis + JSON)
  // Find the last complete JSON object in the response
  const jsonMatches = raw.match(/\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g);
  if (jsonMatches && jsonMatches.length > 0) {
    // Try the last match first (most likely to be complete)
    for (let i = jsonMatches.length - 1; i >= 0; i--) {
      try {
        return JSON.parse(jsonMatches[i]);
      } catch (e) {
        continue;
      }
    }
  }

  // Strategy 3: Handle analysis text before JSON (the main issue)
  // Look for patterns like "analysisWe need to..." followed by JSON
  const analysisPattern = /analysis.*?(\{[\s\S]*?\})/is;
  const analysisMatch = raw.match(analysisPattern);
  if (analysisMatch) {
    try {
      return JSON.parse(analysisMatch[1]);
    } catch (e) {
      // Continue to other strategies
    }
  }

  // Strategy 3.5: Handle the exact pattern from console logs
  // "analysisWe need to output ONLY the JSON object...assistantfinal{...}"
  if (raw.includes('assistantfinal')) {
    const finalIndex = raw.indexOf('assistantfinal');
    const jsonPart = raw.substring(finalIndex + 'assistantfinal'.length);
    try {
      return JSON.parse(jsonPart);
    } catch (e) {
      // Continue to other strategies
    }
  }

  // Strategy 3.6: Handle analysis prefix with direct JSON extraction
  if (raw.startsWith('analysis')) {
    const jsonStart = raw.indexOf('{');
    if (jsonStart > -1) {
      const jsonPart = raw.substring(jsonStart);
      try {
        return JSON.parse(jsonPart);
      } catch (e) {
        // Continue to other strategies
      }
    }
  }

  // Strategy 3: Extract from mixed content (analysis + JSON)
  // Look for patterns like "analysis...{json}" or "analysis...```json{json}```"
  const mixedPatterns = [
    /(?:analysis|assistant|final)\s*\{[\s\S]*\}/i,
    /\{[\s\S]*?\}(?=\s*$)/,
    /\{[\s\S]*?\}(?=\s*(?:analysis|assistant|final))/i
  ];

  for (const pattern of mixedPatterns) {
    const match = raw.match(pattern);
    if (match) {
      let candidate = match[0];
      
      // Clean up the candidate
      candidate = candidate
        .replace(/^(?:analysis|assistant|final)\s*/i, '')
        .trim();
      
      // Find the JSON object within the candidate
      const jsonStart = candidate.indexOf('{');
      const jsonEnd = candidate.lastIndexOf('}');
      
      if (jsonStart >= 0 && jsonEnd > jsonStart) {
        candidate = candidate.substring(jsonStart, jsonEnd + 1);
        
        try {
          return JSON.parse(candidate) as T;
        } catch (e) {
          console.log('Mixed content JSON parsing failed');
        }
      }
    }
  }

  // Strategy 4: Aggressive extraction - find any JSON-like structure
  const aggressiveMatch = raw.match(/\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/);
  if (aggressiveMatch) {
    try {
      let candidate = aggressiveMatch[0];
      
      // Basic JSON fixes
      candidate = candidate
        // Fix unquoted property names
        .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)(\s*:)/g, '$1"$2"$3')
        // Fix single quotes
        .replace(/'/g, '"')
        // Remove trailing commas
        .replace(/,(\s*[}\]])/g, '$1')
        // Remove control characters
        .replace(/[\x00-\x1F\x7F-\x9F]/g, '');
      
      return JSON.parse(candidate) as T;
    } catch (e) {
      console.log('Aggressive extraction failed');
    }
  }

  // Strategy 5: Handle truncated JSON by completing it
  // Look for incomplete JSON that starts properly but gets cut off
  const incompleteJsonMatch = raw.match(/\{\s*"viabilityScore"[\s\S]*$/);
  if (incompleteJsonMatch) {
    try {
      let candidate = incompleteJsonMatch[0];
      
      // Count open vs closed brackets to see if it's incomplete
      const openBraces = (candidate.match(/\{/g) || []).length;
      const closeBraces = (candidate.match(/\}/g) || []).length;
      const openBrackets = (candidate.match(/\[/g) || []).length;
      const closeBrackets = (candidate.match(/\]/g) || []).length;
      
      // If it's incomplete, try to close it properly
      if (openBraces > closeBraces || openBrackets > closeBrackets) {
        // Remove any trailing incomplete content
        candidate = candidate.replace(/,\s*$/, '');
        
        // Add missing closing brackets/braces
        if (openBrackets > closeBrackets) {
          candidate += ']'.repeat(openBrackets - closeBrackets);
        }
        if (openBraces > closeBraces) {
          candidate += '}'.repeat(openBraces - closeBraces);
        }
        
        // If still missing required fields, add them
        if (!candidate.includes('"sources"')) {
          candidate = candidate.replace(/}$/, ',"sources":[]}');
        }
      }
      
      return JSON.parse(candidate) as T;
    } catch (e) {
      console.log('Truncated JSON completion failed');
    }
  }

  // Strategy 6: Last resort - try to build valid JSON from fragments
  const lines = raw.split('\n').filter(line => line.trim());
  const jsonLines = lines.filter(line => 
    line.includes(':') || 
    line.includes('{') || 
    line.includes('}') ||
    line.includes('[') ||
    line.includes(']')
  );
  
  if (jsonLines.length > 0) {
    try {
      let reconstructed = jsonLines.join('\n');
      
      // Ensure it starts and ends properly
      if (!reconstructed.trim().startsWith('{')) {
        reconstructed = '{' + reconstructed;
      }
      if (!reconstructed.trim().endsWith('}')) {
        reconstructed = reconstructed + '}';
      }
      
      // Basic cleanup
      reconstructed = reconstructed
        .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)(\s*:)/g, '$1"$2"$3')
        .replace(/'/g, '"')
        .replace(/,(\s*[}\]])/g, '$1');
      
      return JSON.parse(reconstructed) as T;
    } catch (e) {
      console.log('Reconstruction failed');
    }
  }

  // If all strategies fail, throw error
  console.error('All JSON parsing strategies failed for:', raw);
  throw new Error('No valid JSON object/array found in response');
};

// Helper: build a minimal detailed assessment from summary results so tabs are not empty
const buildFallbackDetailed = (summary: AnalysisResults | null): DetailedAssessment => {
  const recs = summary?.recommendations ?? [];
  const strengths = recs.slice(0, 3);
  const weaknesses = ['Clarify ICP and pricing', 'Validate demand with real users', 'Differentiate from incumbents'];
  const opportunities = ['Leverage niche communities', 'Partner with creators', 'Automate manual workflows'];
  const threats = ['Strong incumbents', 'Acquisition costs rising', 'Copycat risk'];
  return {
    swot: { strengths, weaknesses, opportunities, threats },
    mvpSteps: [
      'Ship a simple landing page and waitlist',
      'Collect 20+ qualitative interviews',
      'Build a clickable prototype',
      'Test pricing on 5-10 prospects',
    ],
    reachTarget: [
      'Post value threads in relevant communities',
      'DM early adopters with clear offer',
      'Run 2 micro-experiments for activation',
    ],
    marketDemand: { summary: 'Moderate interest expected with focused ICP.', signals: ['Search trends', 'Community discussions', 'Competitor traction'] },
    marketCompetition: {
      summary: 'Several adjacent tools; differentiation via focus and speed.',
      competitors: [
        { name: 'Incumbent A', note: 'Similar features; higher price' },
        { name: 'Incumbent B', note: 'Broad tool; less focused' },
      ],
    },
    frameworks: { leanCanvas: ['Problem', 'Solution', 'Unique value', 'Revenue'], aarr: ['Acquisition', 'Activation', 'Retention', 'Revenue'] },
    actionPlan: {
      next30Days: ['Validate ICP', 'Ship MVP v0.1', 'Collect first 10 users'],
      risks: ['No clear demand', 'Long build cycles'],
      mitigations: ['Short build loops', 'Talk to users weekly'],
    },
    sources: summary?.sources ?? [],
  };
};

//


interface IdeaAssessmentProps {
  // Component props if needed
}

const IdeaAssessment: React.FC<IdeaAssessmentProps> = () => {
  const [activeTab, setActiveTab] = useState('form');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);
  const [detailedAssessment, setDetailedAssessment] = useState<DetailedAssessment | null>(null);
  const [isGeneratingDetailed, setIsGeneratingDetailed] = useState(false);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const [generationProgress, setGenerationProgress] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const { user } = useAuth();
  const { refreshData } = useUserData();

  // Synchronous initial hydration from localStorage to avoid empty first render
  const getInitialFormData = (): IdeaAssessmentData => {
    try {
      const baseDraftKey = 'idea_assessment_draft';
      const perUserKey = user?.id ? `${baseDraftKey}_${user.id}` : undefined;
      const rawUser = perUserKey ? localStorage.getItem(perUserKey) : null;
      const rawBase = localStorage.getItem(baseDraftKey);
      const raw = rawUser || rawBase;
      if (raw) {
        const draft = JSON.parse(raw);
        return {
          id: undefined,
          user_id: user?.id || draft.user_id || '',
          idea_description: draft.idea_description || '',
          target_audience: draft.target_audience || '',
          problem_solved: draft.problem_solved || '',
          revenue_model: draft.revenue_model || '',
          competition: draft.competition || '',
          why_people_pay: draft.why_people_pay || '',
          ai_feedback: undefined,
          created_at: undefined,
          updated_at: undefined,
          status: 'draft'
        } as IdeaAssessmentData;
      }
    } catch {}
    return {
      id: undefined,
      user_id: user?.id || '',
      idea_description: '',
      target_audience: '',
      problem_solved: '',
      revenue_model: '',
      competition: '',
      why_people_pay: '',
      ai_feedback: undefined,
      created_at: undefined,
      updated_at: undefined,
      status: 'draft'
    } as IdeaAssessmentData;
  };
  const [formData, setFormData] = useState<IdeaAssessmentData>({
    id: '',
    user_id: '',
    idea_description: '',
    target_audience: '',
    problem_solved: '',
    revenue_model: '',
    competition: '',
    why_people_pay: '',
    created_at: '',
    updated_at: '',
    ai_feedback: null,
    status: 'draft'
  });

  // Derived flag: unlock tabs when minimum required fields are filled OR AI results exist
  const hasIdeaInfo = useMemo(() => {
    const essentials: Array<keyof IdeaAssessmentData> = [
      'idea_description',
      'target_audience',
      'problem_solved',
    ];
    const minFilled = essentials.every((k) => {
      const v = formData[k];
      return typeof v === 'string' && v.trim().length > 0;
    });
    return minFilled || !!detailedAssessment || !!analysisResults;
  }, [formData, detailedAssessment, analysisResults]);

  // Keys for storing unsaved draft locally
  const baseDraftKey = 'idea_assessment_draft';
  const draftKey = user?.id ? `${baseDraftKey}_${user.id}` : baseDraftKey;

  // Prevent writing empty draft before we've tried loading from DB/localStorage
  const draftInitializedRef = useRef(false);
  // Prevent late rehydrate/DB loads from overriding in-progress typing
  const userEditedRef = useRef(false);

  // Helper to load draft from localStorage
  const loadDraft = () => {
    try {
      // Try per-user draft first, then fall back to base draft
      const rawUser = localStorage.getItem(draftKey);
      const rawBase = localStorage.getItem(baseDraftKey);
      const raw = rawUser || rawBase;
      if (raw) {
        const draft = JSON.parse(raw) as Partial<IdeaAssessmentData>;
        // Do not overwrite if user started typing
        if (!userEditedRef.current) {
          setFormData(prev => ({
            ...prev,
            ...draft,
            user_id: user?.id || prev.user_id,
          }));
        }
        setHydrated(true);
      }
      // Mark draft initialization attempted
      draftInitializedRef.current = true;
    } catch (e) {
      console.warn('Failed to load draft from localStorage:', e);
    }
  };

  // Helper to check if all editable fields are empty
  const isFormCompletelyEmpty = (state: IdeaAssessmentData) => {
    return [
      state.idea_description,
      state.target_audience,
      state.problem_solved,
      state.revenue_model,
      state.competition,
      state.why_people_pay,
    ].every((v) => !v || v.trim().length === 0);
  };

  // Rehydrate from draft when user returns to the tab/window
  useEffect(() => {
    const tryRehydrate = () => {
      // Always load from draft to ensure it wins races when coming back
      loadDraft();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tryRehydrate();
    };
    window.addEventListener('focus', tryRehydrate);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', tryRehydrate);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Save one last time before page/tab unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        const { id, created_at, updated_at, ai_feedback, ...draft } = formData as any;
        const payload = JSON.stringify(draft);
        localStorage.setItem('idea_assessment_draft', payload);
      } catch {}
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formData]);

  // Load any local draft immediately on first mount (before auth resolves)
  useEffect(() => {
    loadDraft();
    // Rehydrate again shortly after mount to beat any late effects
    const t = setTimeout(() => loadDraft(), 250);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  return () => clearTimeout(t);
  }, []);

  // When user becomes available, migrate base draft to per-user key if needed
  useEffect(() => {
    if (!user?.id) return;
    try {
      const userHasDraft = !!localStorage.getItem(draftKey);
      const baseHasDraft = !!localStorage.getItem(baseDraftKey);
      if (!userHasDraft && baseHasDraft) {
        const raw = localStorage.getItem(baseDraftKey);
        if (raw) {
          localStorage.setItem(draftKey, raw);
        }
      }
    } catch (e) {
      console.warn('Failed to migrate draft to per-user key:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Load existing assessment data on component mount
  useEffect(() => {
    const loadExistingAssessment = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('idea_assessments')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        if (error) {
          throw error;
        }
        
        if (data) {
          // If a non-empty draft exists locally, prefer it over DB to avoid overwriting user edits
          try {
            const rawUser = localStorage.getItem(draftKey);
            const rawBase = localStorage.getItem(baseDraftKey);
            const raw = rawUser || rawBase;
            if (raw) {
              const draft = JSON.parse(raw) as Partial<IdeaAssessmentData>;
              const hasAny = [
                draft.idea_description,
                draft.target_audience,
                draft.problem_solved,
                draft.revenue_model,
                draft.competition,
                (draft as any).why_people_pay,
              ].some(v => typeof v === 'string' && v.trim().length > 0);
              if (hasAny) {
                draftInitializedRef.current = true;
                setHydrated(true);
                // Still load AI data even if using draft
                if ((data as any).ai_feedback) {
                  setAnalysisResults((data as any).ai_feedback);
                  setShowResults(true);
                }
                if ((data as any).assessment_data) {
                  setDetailedAssessment((data as any).assessment_data);
                }
                return;
              }
            }
          } catch {}

          setFormData({
            id: data.id,
            user_id: data.user_id,
            idea_description: data.idea_description || '',
            target_audience: data.target_audience || '',
            problem_solved: data.problem_solved || '',
            revenue_model: data.revenue_model || '',
            competition: data.competition || '',
            why_people_pay: (data as any).why_people_pay ?? ''
          });
          draftInitializedRef.current = true;
          setHydrated(true);
          
          // Load AI analysis if it exists
          if ((data as any).ai_feedback) {
            setAnalysisResults((data as any).ai_feedback);
            setShowResults(true);
          }
          
          // Load detailed assessment if it exists - this ensures data persists across tabs
          if ((data as any).assessment_data) {
            setDetailedAssessment((data as any).assessment_data);
          }
        } else {
          // No DB data - attempt to hydrate from local draft
          loadDraft();
          // If there was no draft either, mark hydrated so UI renders stable
          setHydrated(true);
        }
      } catch (error) {
        console.error('Error loading existing assessment:', error);
        setHydrated(true);
      }
    };

    loadExistingAssessment();
  }, [user]);

  const tabs = [
    { id: 'form', label: 'Assessment Form', icon: Target },
    { id: 'swot', label: 'SWOT Analysis', icon: TrendingUp },
    { id: 'mvp', label: 'Build MVP', icon: Lightbulb },
    { id: 'reach', label: 'Reach Target', icon: Users },
    { id: 'market', label: 'Market Demand', icon: DollarSign },
    { id: 'competition', label: 'Market Competition', icon: Zap },
    { id: 'frameworks', label: 'Frameworks', icon: Target },
    { id: 'action', label: 'Action Plan', icon: CheckCircle }
  ];

  // Safely compute progress only over known string fields
  const completedCount = () => {
    const fields: Array<keyof IdeaAssessmentData> = [
      'idea_description',
      'target_audience',
      'problem_solved',
      'revenue_model',
      'competition',
      'why_people_pay',
    ];
    return fields.filter((key) => {
      const val = formData[key];
      return typeof val === 'string' && val.trim().length > 0;
    }).length;
  };

  // Minimal fields required to run AI analysis without forcing a DB save
  const isMinimumFilled = () => {
    const essentials: Array<keyof IdeaAssessmentData> = [
      'idea_description',
      'target_audience',
      'problem_solved',
    ];
    return essentials.every((k) => {
      const v = formData[k];
      return typeof v === 'string' && v.trim().length > 0;
    });
  };

  // Pre-paint hydration to ensure draft applies before first paint
  useLayoutEffect(() => {
    try {
      // Try per-user draft first, then base
      const rawUser = localStorage.getItem(draftKey);
      const rawBase = localStorage.getItem(baseDraftKey);
      const raw = rawUser || rawBase;
      if (raw) {
        const draft = JSON.parse(raw);
        setFormData(prev => ({ ...prev, ...draft, user_id: user?.id || prev.user_id }));
        draftInitializedRef.current = true;
        setHydrated(true);
      }
    } catch {}
  // include draftKey so when auth resolves to a user, we can pre-hydrate correctly on first mount of that session
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  // Persist draft to localStorage whenever user edits the form
  useEffect(() => {
    try {
      // Avoid overwriting with empty data before initialization
      if (!draftInitializedRef.current) return;

      const { id, created_at, updated_at, ai_feedback, ...draft } = formData as any;
      const payload = JSON.stringify(draft);
      // Skip saving if all fields are empty strings
      const allEmpty = [
        draft.idea_description,
        draft.target_audience,
        draft.problem_solved,
        draft.revenue_model,
        draft.competition,
        draft.why_people_pay,
      ].every((v: string | undefined) => !v || v.trim().length === 0);
      if (allEmpty) return;
      // Always save to base key for early typing before auth, also save to per-user if available
      localStorage.setItem(baseDraftKey, payload);
      localStorage.setItem(draftKey, payload);
    } catch (e) {
      console.warn('Failed to save draft to localStorage:', e);
    }
  }, [formData, draftKey]);

  const handleInputChange = (field: keyof IdeaAssessmentData, value: string) => {
    userEditedRef.current = true;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveIdea = async () => {
    if (!user?.id) return;
    
    setIsSaving(true);
    try {
      // Build payload explicitly to avoid sending non-existent columns
      const dataToSave = {
        user_id: user.id,
        idea_description: formData.idea_description,
        target_audience: formData.target_audience,
        problem_solved: formData.problem_solved,
        revenue_model: formData.revenue_model,
        competition: formData.competition,
        // DB column name is why_people_pay
        why_people_pay: formData.why_people_pay,
        updated_at: new Date().toISOString()
      };

      let result;
      if (formData.id) {
        // Update existing record
        result = await supabase
          .from('idea_assessments')
          .update(dataToSave)
          .eq('id', formData.id)
          .eq('user_id', user.id)
          .select()
          .single();
      } else {
        // Insert new record
        result = await supabase
          .from('idea_assessments')
          .insert([{
            ...dataToSave,
            created_at: new Date().toISOString()
          }])
          .select()
          .single();
      }
      
      if (result.error) throw result.error;
      
      if (result.data) {
        setFormData(prev => ({ ...prev, id: result.data.id }));
        // Mark profile as validated so other modules unlock
        try {
          if (user?.id) {
            await dataService.updateUserProfile(user.id, { idea_validated: true });
            // Ensure user is assigned to a pod and has baseline data
            await dataService.initializeUserData(user.id);
            // Only refresh data if this is the first time saving, not on every save
            // This prevents AI results from disappearing when user clicks "Save Progress"
            if (!formData.id) {
              await refreshData();
            }
          }
        } catch (e) {
          console.warn('Post-save init failed (flag/pod/tasks):', e);
        }
        // Clear draft after successful save (both per-user and base keys)
        try {
          localStorage.removeItem(draftKey);
          localStorage.removeItem(baseDraftKey);
        } catch {}
        
        // Show success message
        const successDiv = document.createElement('div');
        successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
        successDiv.textContent = '✅ Assessment saved successfully!';
        document.body.appendChild(successDiv);
        
        setTimeout(() => {
          document.body.removeChild(successDiv);
        }, 3000);
      }
      
    } catch (error) {
      console.error('Error saving idea:', error);
      
      // Show error message
      const errorDiv = document.createElement('div');
      errorDiv.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
      errorDiv.textContent = '❌ Failed to save assessment. Please try again.';
      document.body.appendChild(errorDiv);
      
      setTimeout(() => {
        if (document.body.contains(errorDiv)) {
          document.body.removeChild(errorDiv);
        }
      }, 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const parseAIResponse = (response: string): AnalysisResults => {
    try {
      if (!response || typeof response !== 'string') {
        throw new Error('Empty or invalid response');
      }

      // Log the raw response for debugging
      console.log('Raw AI response:', response);

      // Use the improved parseSafeJson function
      const parsed = parseSafeJson(response);

      // Validate required fields and structure
      const result: AnalysisResults = {
        viabilityScore: typeof parsed.viabilityScore === 'number' ? parsed.viabilityScore : 7,
        marketPotential: ['Low', 'Medium', 'High'].includes(parsed.marketPotential) 
          ? parsed.marketPotential 
          : 'Medium',
        competitionLevel: ['Low', 'Medium', 'High'].includes(parsed.competitionLevel)
          ? parsed.competitionLevel
          : 'Medium',
        recommendations: Array.isArray(parsed.recommendations) 
          ? parsed.recommendations.filter((r: any) => typeof r === 'string')
          : [
              'Validate your assumptions with potential customers',
              'Build a minimum viable product (MVP) to test the market',
              'Research your competition thoroughly',
              'Consider your pricing strategy carefully',
              'Focus on customer acquisition channels',
              'Plan your go-to-market strategy'
            ],
        sources: Array.isArray(parsed.sources) 
          ? parsed.sources.filter((s: any) => typeof s === 'string')
          : []
      };
      
      return result;
      
    } catch (error) {
      console.error('Error parsing AI response:', error);
      // Return a fallback response if parsing fails
      return {
        viabilityScore: 7,
        marketPotential: 'Medium',
        competitionLevel: 'Medium',
        recommendations: [
          'Validate your assumptions with potential customers',
          'Build a minimum viable product (MVP) to test the market',
          'Research your competition thoroughly',
          'Consider your pricing strategy carefully',
          'Focus on customer acquisition channels',
          'Plan your go-to-market strategy'
        ],
        sources: ['Fallback response due to parsing error']
      };
    }
  };

  // Generate analysis for specific tab
  const generateTabAnalysis = async (tabType: string) => {
    if (!isMinimumFilled()) {
      alert('Please complete the basic assessment first.');
      return;
    }

    setLoadingStates(prev => ({ ...prev, [tabType]: true }));
    
    try {
      let prompt = '';
      let expectedFormat = '';
      
      switch (tabType) {
        case 'swot':
          prompt = `Analyze this startup idea and provide a detailed SWOT analysis:\n\nIdea: ${formData.idea_description}\nTarget Audience: ${formData.target_audience}\nProblem: ${formData.problem_solved}\nRevenue Model: ${formData.revenue_model}\nCompetition: ${formData.competition}\nWhy Pay: ${formData.why_people_pay}`;
          expectedFormat = `{"strengths": ["..."], "weaknesses": ["..."], "opportunities": ["..."], "threats": ["..."]}`;
          break;
        case 'mvp':
          prompt = `Create a step-by-step MVP development plan for this startup idea:\n\nIdea: ${formData.idea_description}\nTarget Audience: ${formData.target_audience}\nProblem: ${formData.problem_solved}`;
          expectedFormat = `["Step 1: ...", "Step 2: ...", "Step 3: ..."]`;
          break;
        case 'reach':
          prompt = `Create a strategy to reach and acquire the target audience:\n\nIdea: ${formData.idea_description}\nTarget Audience: ${formData.target_audience}\nProblem: ${formData.problem_solved}`;
          expectedFormat = `["Strategy 1: ...", "Strategy 2: ...", "Strategy 3: ..."]`;
          break;
        case 'market':
          prompt = `Analyze market demand for this startup idea:\n\nIdea: ${formData.idea_description}\nTarget Audience: ${formData.target_audience}\nProblem: ${formData.problem_solved}`;
          expectedFormat = `{"summary": "...", "signals": ["...", "..."]}`;
          break;
        case 'competition':
          prompt = `Analyze the competitive landscape for this startup idea:\n\nIdea: ${formData.idea_description}\nTarget Audience: ${formData.target_audience}\nCompetition: ${formData.competition}`;
          expectedFormat = `{"summary": "...", "competitors": [{"name": "...", "note": "..."}]}`;
          break;
        case 'frameworks':
          prompt = `Apply Lean Canvas and AARRR frameworks to this startup idea:\n\nIdea: ${formData.idea_description}\nTarget Audience: ${formData.target_audience}\nProblem: ${formData.problem_solved}\nRevenue Model: ${formData.revenue_model}`;
          expectedFormat = `{"leanCanvas": ["..."], "aarr": ["..."]}`;
          break;
        case 'action':
          prompt = `Create a 30-day action plan with risks and mitigations for this startup idea:\n\nIdea: ${formData.idea_description}\nTarget Audience: ${formData.target_audience}\nProblem: ${formData.problem_solved}`;
          expectedFormat = `{"next30Days": ["..."], "risks": ["..."], "mitigations": ["..."]}`;
          break;
        default:
          throw new Error('Unknown tab type');
      }

      // Call Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('openrouter-ai', {
        body: {
          messages: [
            {
              role: 'system',
              content: 'You are an expert startup advisor. Return ONLY valid JSON in the exact format requested. No additional text, explanations, or formatting.'
            },
            {
              role: 'user',
              content: `${prompt}\n\nReturn ONLY JSON in this format: ${expectedFormat}`
            }
          ],
          model: 'openai/gpt-4o-mini',
          temperature: 0.7,
          max_tokens: 800
        }
      });

      if (error) {
        throw new Error(`API error: ${error.message}`);
      }

      const content = data.choices[0]?.message?.content;
      
      if (!content) {
        throw new Error('No content received from API');
      }

      // Simple JSON parsing - extract JSON from response
      let jsonStr = content.trim();
      const jsonMatch = jsonStr.match(/\{[\s\S]*\}|\[[\s\S]*\]/);
      if (jsonMatch) {
        jsonStr = jsonMatch[0];
      }

      const parsedData = JSON.parse(jsonStr);
      
      // Update the specific part of detailedAssessment
      setDetailedAssessment(prev => {
        const updated = { ...prev };
        switch (tabType) {
          case 'swot':
            updated.swot = parsedData;
            break;
          case 'mvp':
            updated.mvpSteps = parsedData;
            break;
          case 'reach':
            updated.reachTarget = parsedData;
            break;
          case 'market':
            updated.marketDemand = parsedData;
            break;
          case 'competition':
            updated.marketCompetition = parsedData;
            break;
          case 'frameworks':
            updated.frameworks = parsedData;
            break;
          case 'action':
            updated.actionPlan = parsedData;
            break;
        }
        return updated;
      });
      
      // Save to Supabase immediately after updating state
      if (user?.id && formData.id) {
        // Get the current detailedAssessment and update it with new data
        const currentAssessment = detailedAssessment || {};
        const updatedAssessment = { ...currentAssessment };
        
        switch (tabType) {
          case 'swot':
            updatedAssessment.swot = parsedData;
            break;
          case 'mvp':
            updatedAssessment.mvpSteps = parsedData;
            break;
          case 'reach':
            updatedAssessment.reachTarget = parsedData;
            break;
          case 'market':
            updatedAssessment.marketDemand = parsedData;
            break;
          case 'competition':
            updatedAssessment.marketCompetition = parsedData;
            break;
          case 'frameworks':
            updatedAssessment.frameworks = parsedData;
            break;
          case 'action':
            updatedAssessment.actionPlan = parsedData;
            break;
        }
        
        await supabase
          .from('idea_assessments')
          .update({
            assessment_data: updatedAssessment,
            updated_at: new Date().toISOString()
          })
          .eq('id', formData.id);
      }
      
      // Show success confirmation popup - SECURE (no innerHTML XSS vulnerability)
      const confirmDiv = document.createElement('div');
      confirmDiv.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50';
      
      // Create elements securely without innerHTML
      const modal = document.createElement('div');
      modal.className = 'bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl';
      
      const center = document.createElement('div');
      center.className = 'text-center';
      
      const iconDiv = document.createElement('div');
      iconDiv.className = 'w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4';
      
      // Create SVG element securely without innerHTML
      const svg = document.createElement('svg');
      svg.className = 'w-8 h-8 text-green-500';
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('viewBox', '0 0 24 24');
      
      const path = document.createElement('path');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      path.setAttribute('stroke-width', '2');
      path.setAttribute('d', 'M5 13l4 4L19 7');
      
      svg.appendChild(path);
      iconDiv.appendChild(svg);
      
      const title = document.createElement('h3');
      title.className = 'text-lg font-semibold text-gray-900 mb-2';
      title.textContent = `${tabType.toUpperCase()} Analysis Complete!`;
      
      const message = document.createElement('p');
      message.className = 'text-gray-600 mb-6';
      message.textContent = 'Your ' + tabType + ' analysis has been generated and saved successfully.';
      
      const button = document.createElement('button');
      button.className = 'bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium transition-colors';
      button.textContent = 'Continue';
      button.onclick = () => confirmDiv.remove();
      
      center.appendChild(iconDiv);
      center.appendChild(title);
      center.appendChild(message);
      center.appendChild(button);
      modal.appendChild(center);
      confirmDiv.appendChild(modal);
      document.body.appendChild(confirmDiv);
      
    } catch (error) {
      console.error('Error in generateTabAnalysis:', error);
      
      // Set default analysis results to prevent UI breakage
      setAnalysisResults({
        viabilityScore: 7.0,
        marketPotential: 'Medium',
        competitionLevel: 'Medium',
        recommendations: [
          'AI analysis is currently unavailable. Here are some general tips:',
          'Validate your idea with potential customers',
          'Research your target market thoroughly',
          'Analyze your competition',
          'Define your unique value proposition'
        ],
        sources: []
      });
      
      // Generate a basic detailed assessment
      setDetailedAssessment(buildFallbackDetailed(null));
      setShowResults(true);
      
      // Only show error in development
      if (process.env.NODE_ENV === 'development') {
        const errorDiv = document.createElement('div');
        errorDiv.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
        errorDiv.textContent = `❌ ${error.message || 'Failed to generate AI analysis'}`;
        document.body.appendChild(errorDiv);
        
        setTimeout(() => {
          if (document.body.contains(errorDiv)) {
            document.body.removeChild(errorDiv);
          }
        }, 5000);
      }
    } finally {
      setLoadingStates(prev => ({ ...prev, [tabType]: false }));
    }
  };

  const handleGetAIFeedback = async () => {
    if (!user?.id) {
      alert('Please sign in to get AI feedback.');
      return;
    }

    if (!isMinimumFilled()) {
      alert('Please fill in at least the idea description, target audience, and problem solved.');
      return;
    }

    setIsAnalyzing(true);
    setGenerationProgress(0);
    
    // Show progress indicator - SECURE (no innerHTML XSS vulnerability)
    const progressDiv = document.createElement('div');
    progressDiv.id = 'ai-progress-indicator';
    progressDiv.className = 'fixed top-4 left-1/2 transform -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 min-w-80';
    
    // Create elements securely without innerHTML
    const flexContainer = document.createElement('div');
    flexContainer.className = 'flex items-center space-x-3';
    
    const spinner = document.createElement('div');
    spinner.className = 'animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500';
    
    const textContainer = document.createElement('div');
    
    const title = document.createElement('div');
    title.className = 'font-medium text-gray-900';
    title.textContent = 'Generating AI Analysis...';
    
    const subtitle = document.createElement('div');
    subtitle.className = 'text-sm text-gray-500';
    subtitle.textContent = 'Step 1: Saving assessment data';
    
    const progressContainer = document.createElement('div');
    progressContainer.className = 'mt-3 bg-gray-200 rounded-full h-2';
    
    const progressBar = document.createElement('div');
    progressBar.id = 'progress-bar';
    progressBar.className = 'bg-blue-500 h-2 rounded-full transition-all duration-300';
    progressBar.style.width = '10%';
    
    textContainer.appendChild(title);
    textContainer.appendChild(subtitle);
    flexContainer.appendChild(spinner);
    flexContainer.appendChild(textContainer);
    progressContainer.appendChild(progressBar);
    progressDiv.appendChild(flexContainer);
    progressDiv.appendChild(progressContainer);
    document.body.appendChild(progressDiv);
    
    const updateProgress = (step: string, percentage: number) => {
      const stepElement = progressDiv.querySelector('.text-sm');
      const progressBar = progressDiv.querySelector('#progress-bar') as HTMLElement;
      if (stepElement) stepElement.textContent = step;
      if (progressBar) progressBar.style.width = `${percentage}%`;
      setGenerationProgress(percentage);
    };

    try {
      // First auto-save the idea before AI analysis
      if (!user?.id) {
        alert('Please log in to save your assessment.');
        return;
      }

      // Auto-save the assessment data
      const dataToSave = {
        user_id: user.id,
        idea_description: formData.idea_description,
        target_audience: formData.target_audience,
        problem_solved: formData.problem_solved,
        revenue_model: formData.revenue_model,
        competition: formData.competition,
        why_people_pay: formData.why_people_pay,
        updated_at: new Date().toISOString()
      };

      let savedIdea;
      if (formData.id) {
        // Update existing record
        const result = await supabase
          .from('idea_assessments')
          .update(dataToSave)
          .eq('id', formData.id)
          .eq('user_id', user.id)
          .select()
          .single();
        
        if (result.error) throw result.error;
        savedIdea = result.data;
      } else {
        // Insert new record
        const result = await supabase
          .from('idea_assessments')
          .insert([{
            ...dataToSave,
            created_at: new Date().toISOString()
          }])
          .select()
          .single();
        
        if (result.error) throw result.error;
        savedIdea = result.data;
        setFormData(prev => ({ ...prev, id: savedIdea.id }));
      }

      updateProgress('Step 2: Generating initial AI analysis...', 25);
      console.log('Generating AI analysis via Supabase Edge Function...');
      
      // Call Supabase Edge Function
      let data, error;
      try {
        const response = await supabase.functions.invoke('openrouter-ai', {
          body: {
            messages: [
              {
                role: 'system',
                content: 'You are an expert startup advisor. Analyze business ideas and return ONLY valid JSON with the exact structure requested. No additional text or formatting.'
              },
              {
                role: 'user',
                content: `Analyze this startup idea and provide assessment:\n\nBusiness Idea: ${formData.idea_description}\nTarget Audience: ${formData.target_audience}\nProblem Solved: ${formData.problem_solved}\nRevenue Model: ${formData.revenue_model}\nCompetition: ${formData.competition}\nWhy Pay: ${formData.why_people_pay}\n\nReturn ONLY valid JSON with this exact structure:\n{\n  "viabilityScore": 1-10,\n  "marketPotential": "Low|Medium|High",\n  "competitionLevel": "Low|Medium|High",\n  "recommendations": ["specific actionable advice"],\n  "sources": ["relevant URLs"]\n}`
              }
            ],
            model: 'openai/gpt-4o-mini',
            temperature: 0.7,
            max_tokens: 1000
          }
        });
        
        data = response.data;
        error = response.error;
      } catch (apiError) {
        console.error('OpenRouter API call failed:', apiError);
        throw new Error('AI service is currently unavailable. Please try again later.');
      }

      if (error || !data) {
        // Provide fallback data instead of throwing an error
        console.warn('AI analysis failed, using fallback data');
        data = {
          choices: [{
            message: {
              content: JSON.stringify({
                viabilityScore: 7.0,
                marketPotential: 'Medium',
                competitionLevel: 'Medium',
                recommendations: [
                  'AI analysis is currently unavailable. Here are some general tips:', 
                  'Validate your idea with potential customers',
                  'Research your target market thoroughly',
                  'Analyze your competition',
                  'Define your unique value proposition'
                ],
                sources: []
              })
            }
          }]
        };
      }

      const aiResponse = data.choices[0]?.message?.content;
      
      if (!aiResponse) {
        throw new Error('No response content from OpenRouter API');
      }

      updateProgress('Step 3: Processing AI response...', 50);
      
      // Parse the AI response with robust error handling
      let parsedResults: AnalysisResults;
      try {
        parsedResults = parseAIResponse(aiResponse);
      } catch (parseError) {
        console.error('Failed to parse AI response, using fallback:', parseError);
        
        // Provide fallback results if parsing fails
        parsedResults = {
          viabilityScore: 7.0,
          marketPotential: 'Medium',
          competitionLevel: 'Medium',
          recommendations: [
            'Validate your idea with potential customers',
            'Research your target market thoroughly',
            'Analyze your competition',
            'Define your unique value proposition'
          ],
          sources: []
        };
        
        // Only show error in development
        if (process.env.NODE_ENV === 'development') {
          const errorDiv = document.createElement('div');
          errorDiv.className = 'fixed top-4 right-4 bg-yellow-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
          errorDiv.textContent = '⚠️ AI response format issue - showing fallback analysis';
          document.body.appendChild(errorDiv);
          
          setTimeout(() => {
            if (document.body.contains(errorDiv)) {
              document.body.removeChild(errorDiv);
            }
          }, 5000);
        }
      }
      
      // Ensure we have valid results
      if (!parsedResults || !parsedResults.recommendations || parsedResults.recommendations.length === 0) {
        parsedResults = {
          viabilityScore: 7.0,
          marketPotential: 'Medium',
          competitionLevel: 'Medium',
          recommendations: [
            'Validate your idea with potential customers',
            'Research your target market thoroughly',
            'Analyze your competition',
            'Define your unique value proposition'
          ],
          sources: []
        };
      }
      
      // Save AI analysis to Supabase if we have a saved idea
      if (user?.id && savedIdea?.id) {
        await supabase
          .from('idea_assessments')
          .update({
            ai_feedback: parsedResults as any,
            updated_at: new Date().toISOString()
          })
          .eq('id', savedIdea.id);

        // Mark user profile as idea validated and initialize user data
        await dataService.updateUserProfile(user.id, { idea_validated: true });
        await dataService.initializeUserData(user.id);
        await refreshData();
      }
      
      // Update UI with the parsed results
      setAnalysisResults(parsedResults);
      setShowResults(true);
      
      updateProgress('Step 5: Analysis complete!', 100);
      
      // Remove progress indicator and show confirmation popup
      setTimeout(() => {
        const progressIndicator = document.getElementById('ai-progress-indicator');
        if (progressIndicator) {
          document.body.removeChild(progressIndicator);
        }
        
        // Show success confirmation popup - SECURE (no innerHTML XSS vulnerability)
        const confirmDiv = document.createElement('div');
        confirmDiv.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50';
        
        // Create elements securely without innerHTML
        const modal = document.createElement('div');
        modal.className = 'bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl';
        
        const center = document.createElement('div');
        center.className = 'text-center';
        
        const iconDiv = document.createElement('div');
        iconDiv.className = 'w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4';
        
        // Create SVG element securely without innerHTML
        const svg = document.createElement('svg');
        svg.className = 'w-8 h-8 text-green-500';
        svg.setAttribute('fill', 'none');
        svg.setAttribute('stroke', 'currentColor');
        svg.setAttribute('viewBox', '0 0 24 24');
        
        const path = document.createElement('path');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        path.setAttribute('stroke-width', '2');
        path.setAttribute('d', 'M5 13l4 4L19 7');
        
        svg.appendChild(path);
        iconDiv.appendChild(svg);
        
        const title = document.createElement('h3');
        title.className = 'text-lg font-semibold text-gray-900 mb-2';
        title.textContent = 'Assessment Analysis Complete!';
        
        const message = document.createElement('p');
        message.className = 'text-gray-600 mb-6';
        message.textContent = 'Your initial assessment analysis has been generated and saved successfully. You can now explore detailed insights in other tabs.';
        
        const button = document.createElement('button');
        button.className = 'bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium transition-colors';
        button.textContent = 'Continue';
        button.onclick = () => confirmDiv.remove();
        
        center.appendChild(iconDiv);
        center.appendChild(title);
        center.appendChild(message);
        center.appendChild(button);
        modal.appendChild(center);
        confirmDiv.appendChild(modal);
        document.body.appendChild(confirmDiv);
      }, 1000);

      // Assessment form generation complete - no auto-continuation to other tabs
      
      // Save AI analysis to Supabase and mark as completed
      if (user && savedIdea?.id) {
        await supabase
          .from('idea_assessments')
          .update({
            ai_feedback: parsedResults as any,
            updated_at: new Date().toISOString()
          })
          .eq('id', savedIdea.id);

        // Mark user profile as idea validated and initialize user data
        await dataService.updateUserProfile(user.id, { idea_validated: true });
        await dataService.initializeUserData(user.id);
        
        // Generate startup methodology tasks for validated user
        const { startupTaskGenerator } = await import('@/services/startupTaskGenerator');
        const hasStartupTasks = await startupTaskGenerator.hasStartupTasks(user.id);
        if (!hasStartupTasks) {
          console.log('Generating 30-day startup methodology tasks...');
          await startupTaskGenerator.generateStartupTasks(user.id);
        }
        
        await refreshData();

        // Clear localStorage draft since assessment is now complete
        try {
          localStorage.removeItem(draftKey);
          localStorage.removeItem(baseDraftKey);
        } catch {}

        // Show success message
        const successDiv = document.createElement('div');
        successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
        successDiv.textContent = '✅ AI Assessment completed and saved!';
        document.body.appendChild(successDiv);
        
        setTimeout(() => {
          if (document.body.contains(successDiv)) {
            document.body.removeChild(successDiv);
          }
        }, 4000);
      }

    } catch (error) {
      console.error('Error getting AI feedback:', error);
      
      // Enhanced fallback analysis based on form data
      const ideaDescription = formData.idea_description.toLowerCase();
      const targetAudience = formData.target_audience.toLowerCase();
      
      // Basic viability scoring based on form completeness and keywords
      let viabilityScore = 5;
      if (formData.idea_description.length > 100) viabilityScore += 1;
      if (formData.target_audience.length > 20) viabilityScore += 1;
      if (formData.problem_solved.length > 50) viabilityScore += 1;
      if (ideaDescription.includes('ai') || ideaDescription.includes('saas')) viabilityScore += 1;
      
      // Market potential assessment
      let marketPotential = 'Medium';
      if (targetAudience.includes('entrepreneur') || targetAudience.includes('business')) {
        marketPotential = 'High';
      } else if (targetAudience.includes('niche') || targetAudience.includes('specific')) {
        marketPotential = 'Low';
      }
      
      // Competition level assessment
      let competitionLevel = 'Medium';
      if (ideaDescription.includes('unique') || ideaDescription.includes('innovative')) {
        competitionLevel = 'Low';
      } else if (ideaDescription.includes('popular') || ideaDescription.includes('common')) {
        competitionLevel = 'High';
      }
      
      // Tailored recommendations based on form data
      const recommendations = [
        `Validate "${formData.problem_solved}" with your target audience: ${formData.target_audience}`,
        'Build a minimum viable product (MVP) focusing on core features',
        `Research competitors in the ${formData.target_audience} market`,
        `Test your pricing strategy for ${formData.revenue_model}`,
        'Create a customer acquisition plan for early adopters',
        'Establish metrics to measure product-market fit'
      ];
      
      setAnalysisResults({
        viabilityScore,
        marketPotential,
        competitionLevel,
        recommendations,
        sources: ['Analysis based on form data - AI service temporarily unavailable']
      });
      setShowResults(true);
      
      // Show user-friendly error message
      const errorDiv = document.createElement('div');
      errorDiv.className = 'fixed top-4 right-4 bg-orange-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
      errorDiv.textContent = '🔄 AI service unavailable - showing smart fallback analysis';
      document.body.appendChild(errorDiv);
      
      setTimeout(() => {
        if (document.body.contains(errorDiv)) {
          document.body.removeChild(errorDiv);
        }
      }, 5000);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const mockSWOTData = {
    strengths: [
      'Unique value proposition in underserved market',
      'Strong technical expertise in team',
      'Low initial capital requirements',
      'Scalable business model'
    ],
    weaknesses: [
      'Limited brand recognition',
      'Small initial team size',
      'Dependence on key personnel',
      'Limited marketing budget'
    ],
    opportunities: [
      'Growing market demand',
      'Potential for strategic partnerships',
      'Expansion into adjacent markets',
      'Technology advancement trends'
    ],
    threats: [
      'Established competitors',
      'Economic downturn risks',
      'Regulatory changes',
      'Technology disruption'
    ]
  };

  const mockMarketData = {
    totalMarket: '$2.5B',
    targetSegment: '$450M',
    growthRate: '15%',
    demandScore: 8.2
  };

  const renderNoDataPrompt = () => (
    <div className="text-center py-12">
      <AlertCircle className="w-16 h-16 text-startup-gold mx-auto mb-4" />
      <h3 className="text-xl font-semibold text-startup-navy mb-2">
        Complete Your Idea Assessment First
      </h3>
      <p className="text-startup-gray mb-6">
        To access this feature, please fill out your idea assessment form first. 
        This will help us provide personalized insights and analysis.
      </p>
      <button
        onClick={() => setActiveTab('form')}
        className="btn-startup-primary"
      >
        Go to Assessment Form
      </button>
    </div>
  );

  // Avoid initial flicker: render nothing until we've hydrated from draft/DB
  if (!hydrated) {
    return <div className="p-6"><div className="max-w-6xl mx-auto"><div className="animate-pulse h-32 bg-startup-slate rounded-2xl" /></div></div>;
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="animate-fade-in mb-8">
          <h1 className="text-3xl font-bold text-startup-navy mb-2">
            Idea Assessment Hub 💡
          </h1>
          <p className="text-startup-gray">
            Get AI-powered insights to assess your business idea and build a winning strategy
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-white rounded-2xl p-2 mb-8 shadow-neumorphic animate-fade-in">
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-3 rounded-xl transition-all duration-300 flex-1 justify-center ${
                activeTab === tab.id
                  ? 'bg-startup-teal text-white shadow-lg'
                  : 'text-startup-gray hover:text-startup-navy hover:bg-startup-slate'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <tab.icon className="w-5 h-5" />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="animate-fade-in">
          {activeTab === 'form' && (
            <div className="space-y-8">
              {/* Header with Save Button */}
              <div className="card-startup">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-3xl font-bold text-startup-navy flex items-center mb-2">
                      <Target className="w-8 h-8 mr-3 text-startup-teal" />
                      Idea Assessment ✨
                    </h2>
                    <p className="text-startup-gray">Complete these tiles to unlock AI-powered insights</p>
                  </div>
                  <button
                    onClick={handleSaveIdea}
                    disabled={isSaving}
                    className="btn-startup-primary flex items-center space-x-2"
                  >
                    <Save className="w-5 h-5" />
                    <span>{isSaving ? 'Saving...' : 'Save Progress'}</span>
                  </button>
                </div>
              </div>

              {/* GenZ-Style Tile Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* Tile 1: Idea Description */}
                <div className="group relative bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] animate-fade-in">
                  <div className="absolute top-4 right-4 text-2xl">💡</div>
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-purple-800 mb-2">What's Your Big Idea?</h3>
                    <p className="text-purple-600 text-sm">Spill the tea! Tell us about your amazing business idea</p>
                  </div>
                  <textarea
                    value={formData.idea_description}
                    onChange={(e) => handleInputChange('idea_description', e.target.value)}
                    placeholder="Your idea is fire 🔥 Tell us all about it..."
                    id="idea_description"
                    name="idea_description"
                    className="w-full p-4 bg-white/70 border-2 border-purple-200 rounded-xl focus:border-purple-400 focus:outline-none resize-none h-32 placeholder:text-purple-400"
                  />
                  <div className="mt-2 text-xs text-purple-500">
                    {formData.idea_description.length}/500 characters
                  </div>
                </div>

                {/* Tile 2: Target Audience */}
                <div className="group relative bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] animate-fade-in" style={{animationDelay: '0.1s'}}>
                  <div className="absolute top-4 right-4 text-2xl">👥</div>
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-blue-800 mb-2">Who's Your Squad?</h3>
                    <p className="text-blue-600 text-sm">Who are you building this for? Your target audience matters!</p>
                  </div>
                  <input
                    type="text"
                    value={formData.target_audience}
                    onChange={(e) => handleInputChange('target_audience', e.target.value)}
                    placeholder="Gen Z entrepreneurs, busy parents, tech nerds..."
                    id="target_audience"
                    name="target_audience"
                    className="w-full p-4 bg-white/70 border-2 border-blue-200 rounded-xl focus:border-blue-400 focus:outline-none placeholder:text-blue-400"
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    {['Gen Z', 'Millennials', 'Small Business', 'Students', 'Professionals'].map((tag) => (
                      <button
                        key={tag}
                        onClick={() => handleInputChange('target_audience', formData.target_audience + (formData.target_audience ? ', ' : '') + tag)}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs hover:bg-blue-200 transition-colors"
                      >
                        + {tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tile 3: Problem Solved */}
                <div className="group relative bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] animate-fade-in" style={{animationDelay: '0.2s'}}>
                  <div className="absolute top-4 right-4 text-2xl">🎯</div>
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-red-800 mb-2">What Problem Are You Solving?</h3>
                    <p className="text-red-600 text-sm">Every great startup fixes something that's broken or annoying</p>
                  </div>
                  <input
                    type="text"
                    value={formData.problem_solved}
                    onChange={(e) => handleInputChange('problem_solved', e.target.value)}
                    placeholder="People struggle with... It's so frustrating when..."
                    id="problem_solved"
                    name="problem_solved"
                    className="w-full p-4 bg-white/70 border-2 border-red-200 rounded-xl focus:border-red-400 focus:outline-none placeholder:text-red-400"
                  />
                  <div className="mt-3 text-xs text-red-500">
                    💡 Tip: The bigger the pain point, the better your solution!
                  </div>
                </div>

                {/* Tile 4: Revenue Model */}
                <div className="group relative bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] animate-fade-in" style={{animationDelay: '0.3s'}}>
                  <div className="absolute top-4 right-4 text-2xl">💰</div>
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-green-800 mb-2">Show Me The Money!</h3>
                    <p className="text-green-600 text-sm">How will you make those dollar bills? 💵</p>
                  </div>
                  <input
                    type="text"
                    value={formData.revenue_model}
                    onChange={(e) => handleInputChange('revenue_model', e.target.value)}
                    placeholder="Monthly subscription, one-time fee, freemium..."
                    id="revenue_model"
                    name="revenue_model"
                    className="w-full p-4 bg-white/70 border-2 border-green-200 rounded-xl focus:border-green-400 focus:outline-none placeholder:text-green-400"
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    {['Subscription', 'One-time', 'Freemium', 'Ads', 'Commission'].map((model) => (
                      <button
                        key={model}
                        onClick={() => handleInputChange('revenue_model', model)}
                        className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs hover:bg-green-200 transition-colors"
                      >
                        {model}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tile 5: Competition */}
                <div className="group relative bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] animate-fade-in" style={{animationDelay: '0.4s'}}>
                  <div className="absolute top-4 right-4 text-2xl">⚔️</div>
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-yellow-800 mb-2">Who's Your Competition?</h3>
                    <p className="text-yellow-600 text-sm">Know your rivals! Who else is in this space?</p>
                  </div>
                  <input
                    type="text"
                    value={formData.competition}
                    onChange={(e) => handleInputChange('competition', e.target.value)}
                    placeholder="Netflix, Spotify, that startup down the street..."
                    id="competition"
                    name="competition"
                    className="w-full p-4 bg-white/70 border-2 border-yellow-200 rounded-xl focus:border-yellow-400 focus:outline-none placeholder:text-yellow-400"
                  />
                  <div className="mt-3 text-xs text-yellow-600">
                    🏆 Competition = validation that there's a market!
                  </div>
                </div>

                {/* Tile 6: Why People Pay */}
                <div className="group relative bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] animate-fade-in" style={{animationDelay: '0.5s'}}>
                  <div className="absolute top-4 right-4 text-2xl">🤑</div>
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-indigo-800 mb-2">Why Will People Pay?</h3>
                    <p className="text-indigo-600 text-sm">What's your secret sauce? Why choose you over free alternatives?</p>
                  </div>
                  <input
                    type="text"
                    value={formData.why_people_pay}
                    onChange={(e) => handleInputChange('why_people_pay', e.target.value)}
                    placeholder="Saves time, makes money, feels amazing..."
                    id="why_people_pay"
                    name="why_people_pay"
                    className="w-full p-4 bg-white/70 border-2 border-indigo-200 rounded-xl focus:border-indigo-400 focus:outline-none placeholder:text-indigo-400"
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    {['Saves Time', 'Makes Money', 'Status Symbol', 'Convenience', 'Quality'].map((reason) => (
                      <button
                        key={reason}
                        onClick={() => handleInputChange('why_people_pay', formData.why_people_pay + (formData.why_people_pay ? ', ' : '') + reason)}
                        className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs hover:bg-indigo-200 transition-colors"
                      >
                        + {reason}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Progress Indicator */}
              <div className="card-startup">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-startup-navy">Assessment Progress</h3>
                  <span className="text-startup-teal font-medium">
                    {completedCount()}/6 completed
                  </span>
                </div>
                <div className="w-full bg-startup-gray/20 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-startup-teal to-startup-gold rounded-full h-3 transition-all duration-500"
                    style={{ width: `${(completedCount() / 6) * 100}%` }}
                  />
                </div>
                <p className="text-startup-gray text-sm mt-2">
                  Complete all fields to unlock AI assessment! 🚀
                </p>
              </div>

              {/* AI Analysis Button */}
              <div className="text-center">
                <button
                  onClick={handleGetAIFeedback}
                  disabled={isAnalyzing || !isMinimumFilled()}
                  className="btn-startup-primary text-lg px-8 py-4"
                >
                  {isAnalyzing ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Analyzing with Cashpurr AI...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Lightbulb className="w-5 h-5" />
                      <span>Get AI Assessment</span>
                    </div>
                  )}
                </button>
              </div>

              {/* AI Results */}
              {showResults && analysisResults && (
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-startup text-white rounded-xl p-6">
                    <h3 className="text-xl font-semibold mb-4">Viability Score</h3>
                    <div className="text-4xl font-bold mb-2">{analysisResults.viabilityScore}/10</div>
                    <p className="text-white/80">Overall assessment of your idea's potential</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-neumorphic">
                    <h3 className="text-xl font-semibold text-startup-navy mb-4">Key Recommendations</h3>
                    <ul className="space-y-2">
                      {Array.isArray(analysisResults.recommendations) && analysisResults.recommendations.map((rec: string, index: number) => (
                        <li key={index} className="flex items-start space-x-2">
                          <CheckCircle className="w-5 h-5 text-startup-teal mt-0.5 flex-shrink-0" />
                          <span className="text-startup-gray">{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {Array.isArray(analysisResults.sources) && analysisResults.sources.length > 0 && (
                    <div className="bg-white rounded-xl p-6 shadow-neumorphic md:col-span-2">
                      <h3 className="text-xl font-semibold text-startup-navy mb-4">Sources</h3>
                      <ul className="list-disc list-inside space-y-1 text-startup-gray break-words">
                        {analysisResults.sources.map((src: string, i: number) => (
                          <li key={i}>
                            <a href={src} target="_blank" rel="noreferrer" className="text-startup-teal hover:underline">{src}</a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'swot' && (
            <div className="card-startup">
              {!hasIdeaInfo ? renderNoDataPrompt() : (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-startup-navy flex items-center">
                      <TrendingUp className="w-6 h-6 mr-3 text-startup-teal" />
                      SWOT Analysis
                    </h2>
                    {hasIdeaInfo && (
                    <button
                      onClick={() => generateTabAnalysis('swot')}
                      disabled={loadingStates.swot}
                      className="btn-primary flex items-center space-x-2 animate-pulse-glow hover:animate-bounce"
                    >
                      <Sparkles className="w-4 h-4 animate-pulse" />
                      <span>{loadingStates.swot ? 'Generating...' : 'Generate SWOT'}</span>
                    </button>
                    )}
                  </div>
                  
                  {/* Loading indicator for SWOT */}
                  {loadingStates.swot && (
                    <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                        <span className="text-blue-700 font-medium">Generating SWOT Analysis...</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Strengths */}
                    <div className="bg-startup-teal/10 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-startup-teal mb-4 flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Strengths
                      </h3>
                      <ul className="space-y-2">
                        {(detailedAssessment?.swot?.strengths || []).map((item, index) => (
                          <li key={index} className="text-startup-navy text-sm">• {item}</li>
                        ))}
                        {!detailedAssessment?.swot && (
                          <li className="text-startup-gray text-sm">Run "Get AI Assessment" to generate SWOT.</li>
                        )}
                      </ul>
                    </div>

                    {/* Weaknesses */}
                    <div className="bg-startup-red/10 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-startup-red mb-4 flex items-center">
                        <AlertCircle className="w-5 h-5 mr-2" />
                        Weaknesses
                      </h3>
                      <ul className="space-y-2">
                        {(detailedAssessment?.swot?.weaknesses || []).map((item, index) => (
                          <li key={index} className="text-startup-navy text-sm">• {item}</li>
                        ))}
                        {!detailedAssessment?.swot && (
                          <li className="text-startup-gray text-sm">Run AI to populate weaknesses.</li>
                        )}
                      </ul>
                    </div>

                    {/* Opportunities */}
                    <div className="bg-startup-gold/10 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-startup-gold mb-4 flex items-center">
                        <Lightbulb className="w-5 h-5 mr-2" />
                        Opportunities
                      </h3>
                      <ul className="space-y-2">
                        {(detailedAssessment?.swot?.opportunities || []).map((item, index) => (
                          <li key={index} className="text-startup-navy text-sm">• {item}</li>
                        ))}
                        {!detailedAssessment?.swot && (
                          <li className="text-startup-gray text-sm">Run AI to populate opportunities.</li>
                        )}
                      </ul>
                    </div>

                    {/* Threats */}
                    <div className="bg-startup-gray/10 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-startup-gray mb-4 flex items-center">
                        <Zap className="w-5 h-5 mr-2" />
                        Threats
                      </h3>
                      <ul className="space-y-2">
                        {(detailedAssessment?.swot?.threats || []).map((item, index) => (
                          <li key={index} className="text-startup-navy text-sm">• {item}</li>
                        ))}
                        {!detailedAssessment?.swot && (
                          <li className="text-startup-gray text-sm">Run AI to populate threats.</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'mvp' && (
            <div className="card-startup">
              {!hasIdeaInfo ? renderNoDataPrompt() : (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-startup-navy flex items-center">
                      <Lightbulb className="w-6 h-6 mr-3 text-startup-teal" />
                      Build MVP Strategy
                    </h2>
                    <button
                      onClick={() => generateTabAnalysis('mvp')}
                      disabled={loadingStates.mvp}
                      className="btn-primary flex items-center space-x-2 animate-pulse-glow hover:animate-bounce"
                    >
                      <Sparkles className="w-4 h-4 animate-pulse" />
                      <span>{loadingStates.mvp ? 'Generating...' : 'Generate MVP'}</span>
                    </button>
                  </div>
                  {loadingStates.mvp && (
                    <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                        <span className="text-blue-700 font-medium">Generating MVP strategy...</span>
                      </div>
                    </div>
                  )}
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-startup-teal to-startup-peach text-white rounded-xl p-6">
                      <h3 className="text-xl font-semibold mb-4">🚀 MVP Steps</h3>
                      <ul className="space-y-2">
                        {(detailedAssessment?.mvpSteps || []).map((s, i) => (
                          <li key={i}>• {s}</li>
                        ))}
                        {!detailedAssessment?.mvpSteps && !loadingStates.mvp && (
                          <li className="text-white/80">Click 'Generate MVP' above to create development steps.</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'reach' && (
            <div className="card-startup">
              {!hasIdeaInfo ? renderNoDataPrompt() : (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-startup-navy flex items-center">
                      <Users className="w-6 h-6 mr-3 text-startup-teal" />
                      Reach Your Target
                    </h2>
                    <button
                      onClick={() => generateTabAnalysis('reach')}
                      disabled={loadingStates.reach}
                      className="btn-primary flex items-center space-x-2 animate-pulse-glow hover:animate-bounce"
                    >
                      <Sparkles className="w-4 h-4 animate-pulse" />
                      <span>{loadingStates.reach ? 'Generating...' : 'Generate Strategy'}</span>
                    </button>
                  </div>
                  {loadingStates.reach && (
                    <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                        <span className="text-blue-700 font-medium">Generating reach strategy...</span>
                      </div>
                    </div>
                  )}
                  <div className="bg-white rounded-xl p-6 shadow-neumorphic">
                    <h3 className="text-lg font-semibold text-startup-navy mb-4">Channels & Tactics</h3>
                    <ul className="space-y-2">
                      {(detailedAssessment?.reachTarget || []).map((x, i) => (
                        <li key={i}>• {x}</li>
                      ))}
                      {!detailedAssessment?.reachTarget && !loadingStates.reach && (
                        <li className="text-startup-gray">Click 'Generate Strategy' to create reach tactics.</li>
                      )}
                    </ul>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'market' && (
            <div className="card-startup">
              {!hasIdeaInfo ? renderNoDataPrompt() : (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-startup-navy flex items-center">
                      <DollarSign className="w-6 h-6 mr-3 text-startup-teal" />
                      Market Demand Analysis
                    </h2>
                    <button
                      onClick={() => generateTabAnalysis('market')}
                      disabled={loadingStates.market}
                      className="btn-primary flex items-center space-x-2 animate-pulse-glow hover:animate-bounce"
                    >
                      <Sparkles className="w-4 h-4 animate-pulse" />
                      <span>{loadingStates.market ? 'Generating...' : 'Analyze Market'}</span>
                    </button>
                  </div>
                  {loadingStates.market && (
                    <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                        <span className="text-blue-700 font-medium">Analyzing market demand...</span>
                      </div>
                    </div>
                  )}
                  <div className="bg-gradient-startup text-white rounded-xl p-6">
                    <h3 className="text-xl font-semibold mb-4">Market Insights</h3>
                    <div className="space-y-3">
                      {detailedAssessment?.marketDemand?.summary ? (
                        <p>• {detailedAssessment.marketDemand.summary}</p>
                      ) : !loadingStates.market ? (
                        <p className="text-white/80">Click 'Analyze Market' to generate market demand insights.</p>
                      ) : null}
                      {(detailedAssessment?.marketDemand?.signals || []).map((s, i) => (
                        <p key={i}>• {s}</p>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'competition' && (
            <div className="card-startup">
              {!hasIdeaInfo ? renderNoDataPrompt() : (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-startup-navy flex items-center">
                      <Zap className="w-6 h-6 mr-3 text-startup-teal" />
                      Competitive Analysis
                    </h2>
                    <button
                      onClick={() => generateTabAnalysis('competition')}
                      disabled={loadingStates.competition}
                      className="btn-primary flex items-center space-x-2 animate-pulse-glow hover:animate-bounce"
                    >
                      <Sparkles className="w-4 h-4 animate-pulse" />
                      <span>{loadingStates.competition ? 'Generating...' : 'Analyze Competition'}</span>
                    </button>
                  </div>
                  {loadingStates.competition && (
                    <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                        <span className="text-blue-700 font-medium">Analyzing competition...</span>
                      </div>
                    </div>
                  )}
                  <div className="space-y-6">
                    <div className="bg-startup-red/10 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-startup-red mb-4">Summary</h3>
                      <p className="text-startup-navy text-sm">
                        {detailedAssessment?.marketCompetition?.summary || 'Run "Get AI Assessment" to generate competition analysis.'}
                      </p>
                    </div>
                    <div className="bg-startup-gold/10 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-startup-gold mb-4">Competitors</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(detailedAssessment?.marketCompetition?.competitors || []).map((c, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-4">
                            <h4 className="font-semibold text-startup-navy">{c.name}</h4>
                            <p className="text-startup-gray text-sm">{c.note}</p>
                          </div>
                        ))}
                        {!detailedAssessment?.marketCompetition && (
                          <div className="text-startup-gray text-sm">No competitors yet. Run AI to populate.</div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'frameworks' && (
            <div className="card-startup">
              {!hasIdeaInfo ? renderNoDataPrompt() : (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-startup-navy flex items-center">
                      <Target className="w-6 h-6 mr-3 text-startup-teal" />
                      Frameworks
                    </h2>
                    <button
                      onClick={() => generateTabAnalysis('frameworks')}
                      disabled={loadingStates.frameworks}
                      className="btn-primary flex items-center space-x-2 animate-pulse-glow hover:animate-bounce"
                    >
                      <Sparkles className="w-4 h-4 animate-pulse" />
                      <span>{loadingStates.frameworks ? 'Generating...' : 'Apply Frameworks'}</span>
                    </button>
                  </div>
                  {loadingStates.frameworks && (
                    <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                        <span className="text-blue-700 font-medium">Applying frameworks...</span>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-startup-peach/10 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-startup-peach mb-4">Lean Canvas</h3>
                      <ul className="space-y-2">
                        {(detailedAssessment?.frameworks?.leanCanvas || []).map((x, i) => (
                          <li key={i}>• {x}</li>
                        ))}
                        {!detailedAssessment?.frameworks && !loadingStates.frameworks && (
                          <li className="text-startup-gray">Click 'Apply Frameworks' to generate Lean Canvas.</li>
                        )}
                      </ul>
                    </div>
                    <div className="bg-startup-navy/10 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-startup-navy mb-4">AARR (Growth)</h3>
                      <ul className="space-y-2">
                        {(detailedAssessment?.frameworks?.aarr || []).map((x, i) => (
                          <li key={i}>• {x}</li>
                        ))}
                        {!detailedAssessment?.frameworks && !loadingStates.frameworks && (
                          <li className="text-startup-gray">Click 'Apply Frameworks' to generate AARR.</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'action' && (
            <div className="card-startup">
              {!hasIdeaInfo ? renderNoDataPrompt() : (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-startup-navy flex items-center">
                      <CheckCircle className="w-6 h-6 mr-3 text-startup-teal" />
                      Action Plan
                    </h2>
                    <button
                      onClick={() => generateTabAnalysis('action')}
                      disabled={loadingStates.action}
                      className="btn-primary flex items-center space-x-2 animate-pulse-glow hover:animate-bounce"
                    >
                      <Sparkles className="w-4 h-4 animate-pulse" />
                      <span>{loadingStates.action ? 'Generating...' : 'Create Action Plan'}</span>
                    </button>
                  </div>
                  {loadingStates.action && (
                    <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                        <span className="text-blue-700 font-medium">Creating action plan...</span>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-startup-teal/10 rounded-xl p-6">
                      <ul className="space-y-2">
                        {(detailedAssessment?.actionPlan?.next30Days || []).map((x, i) => (
                          <li key={i}>• {x}</li>
                        ))}
                        {!detailedAssessment?.actionPlan && !loadingStates.action && (
                          <li className="text-startup-gray">Click 'Create Action Plan' to generate 30-day items.</li>
                        )}
                      </ul>
                    </div>
                    <div className="bg-startup-red/10 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-startup-red mb-4">Risks & Mitigations</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-semibold text-startup-red mb-2">Risks</h4>
                          <ul className="space-y-2">
                            {(detailedAssessment?.actionPlan?.risks || []).map((x, i) => (
                              <li key={i}>• {x}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold text-startup-teal mb-2">Mitigations</h4>
                          <ul className="space-y-2">
                            {(detailedAssessment?.actionPlan?.mitigations || []).map((x, i) => (
                              <li key={i}>• {x}</li>
                            ))}
                            {!detailedAssessment?.actionPlan && (
                              <li className="text-startup-gray">Run AI to generate risks & mitigations.</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  {Array.isArray(detailedAssessment?.sources) && detailedAssessment!.sources!.length > 0 && (
                    <div className="bg-white rounded-xl p-6 shadow-neumorphic mt-6">
                      <h3 className="text-xl font-semibold text-startup-navy mb-4">Sources</h3>
                      <ul className="list-disc list-inside space-y-1 text-startup-gray break-words">
                        {detailedAssessment!.sources!.map((src, i) => (
                          <li key={i}><a className="text-startup-teal hover:underline" href={src} target="_blank" rel="noreferrer">{src}</a></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IdeaAssessment;